"use client";

import type { SliderProps as AriaSliderProps } from "react-aria-components";
import {
	Label as AriaLabel,
	Slider as AriaSlider,
	SliderOutput as AriaSliderOutput,
	SliderThumb as AriaSliderThumb,
	SliderTrack as AriaSliderTrack,
} from "react-aria-components";
import { cx, sortCx } from "@/utils/cx";

interface FuelLevelSliderProps
	extends Omit<
		AriaSliderProps,
		"step" | "minValue" | "maxValue" | "onChange" | "value"
	> {
	value?: number;
	onChange?: (value: number) => void;
	isDisabled?: boolean;
	className?: string;
}

// Valores permitidos no slider (mantém UX do Figma com 4 marcadores principais)
const FUEL_LEVEL_STEPS = [0, 25, 50, 75, 100] as const;
const STEP_SIZE = 25;

// Mesma regra matemática usada no backend (BuildFuelGaugeSvg)
// - Normaliza valor 0..100
// - Calcula quantidade de colunas preenchidas em um total fixo
const getFilledColumnsCount = (value: number, totalColumns: number): number => {
	const clamped = Number.isFinite(value)
		? Math.min(Math.max(value, 0), 100)
		: 0;
	const filled = Math.round((clamped / 100) * totalColumns);
	if (filled < 0) {
		return 0;
	}
	if (filled > totalColumns) {
		return totalColumns;
	}
	return filled;
};

// Função para converter valor numérico em fração
const getFractionLabel = (value: number): string => {
	if (value === 0) {
		return "E";
	}
	if (value === 25) {
		return "1/4";
	}
	if (value === 50) {
		return "1/2";
	}
	if (value === 75) {
		return "3/4";
	}
	if (value === 100) {
		return "F";
	}
	return "";
};

// Função para arredondar para o step mais próximo
const roundToStep = (value: number): number =>
	FUEL_LEVEL_STEPS.reduce((prev, curr) =>
		Math.abs(curr - value) < Math.abs(prev - value) ? curr : prev
	);

export const FuelLevelSlider = ({
	value = 0,
	onChange,
	isDisabled = false,
	className,
	...rest
}: FuelLevelSliderProps) => {
	// Garante que o valor seja arredondado para o step mais próximo
	const roundedValue = roundToStep(value);

	const handleChange = (newValue: number | number[]) => {
		if (onChange) {
			const singleValue = Array.isArray(newValue) ? newValue[0] : newValue;
			const rounded = roundToStep(singleValue);
			onChange(rounded);
		}
	};

	// Calcula quantas colunas devem estar coloridas baseado no valor arredondado
	const totalColumns = 11;
	const filledColumns = getFilledColumnsCount(roundedValue, totalColumns);

	// Função para determinar se uma coluna deve estar colorida
	const isColumnFilled = (index: number) => index < filledColumns;

	// Função para obter a altura da coluna
	const getColumnHeight = (index: number) => {
		// Colunas maiores nas posições 0, 5 (meio), 10 (fim)
		if (index === 0 || index === 5 || index === 10) {
			return "h-[40px]";
		}
		return "h-[26px]";
	};

	// Função para obter a largura da coluna
	const getColumnWidth = (index: number) => {
		// Colunas maiores nas posições 0, 5 (meio), 10 (fim)
		if (index === 0 || index === 5 || index === 10) {
			return "w-[8px]";
		}
		return "w-[4px]";
	};

	const columnStyles = sortCx({
		container: "flex flex-col gap-1.5 items-center max-w-[276px] mx-auto",
		markers:
			"flex w-full justify-center px-6 text-base leading-6 font-semibold text-secondary gap-8 text-primary",
		columnsContainer:
			"flex items-end w-full justify-center gap-4 px-4 pt-1 pb-0.5",
		endLabel: "text-[20px] font-extrabold leading-none text-quaternary",
		column: "shrink-0 transition-colors duration-200 bg-brand-secondary",
	});

	return (
		<div className={cx(columnStyles.container, className)}>
			{/* Marcadores de fração */}
			<div className={columnStyles.markers}>
				<span>1/4</span>
				<span>1/2</span>
				<span>3/4</span>
			</div>

			{/* Container das colunas */}
			<div className={columnStyles.columnsContainer}>
				{/* Letra E (Empty) - esquerda */}
				<span className={columnStyles.endLabel}>E</span>

				{/* Colunas */}
				{Array.from({ length: totalColumns }, (_, index) => {
					const isFilled = isColumnFilled(index);
					const height = getColumnHeight(index);
					const width = getColumnWidth(index);

					return (
						<div
							className={cx(
								columnStyles.column,
								height,
								width,
								isFilled ? "bg-brand-solid" : "bg-quaternary"
							)}
							key={`fuel-column-${index}`}
						/>
					);
				})}

				{/* Letra F (Full) - direita */}
				<span className={columnStyles.endLabel}>F</span>
			</div>

			{/* Slider tradicional */}
			<div className="mx-3 w-[calc(100%-10px)]">
				<AriaSlider
					{...rest}
					aria-label="fuelLevel"
					isDisabled={isDisabled}
					maxValue={100}
					minValue={0}
					onChange={handleChange}
					step={STEP_SIZE}
					value={roundedValue}
				>
					<AriaLabel />
					<AriaSliderTrack className="relative h-6 w-full">
						{({ state: { values, getThumbValue, getThumbPercent } }) => {
							const left = values.length === 1 ? 0 : getThumbPercent(0);
							const width =
								values.length === 1
									? getThumbPercent(0)
									: getThumbPercent(1) - left;

							return (
								<>
									<span className="-translate-y-1/2 absolute top-1/2 h-2 w-full rounded-full bg-quaternary" />
									<span
										className="-translate-y-1/2 absolute top-1/2 h-2 w-full rounded-full bg-brand-solid"
										style={{
											left: `${left * 100}%`,
											width: `${width * 100}%`,
										}}
									/>
									{values.map((_, index) => {
										const thumbValue = getThumbValue(index);
										const roundedThumbValue = roundToStep(thumbValue);
										const fractionLabel = getFractionLabel(roundedThumbValue);

										return (
											<AriaSliderThumb
												className={({ isFocusVisible, isDragging }) =>
													cx(
														"top-1/2 box-border size-6 cursor-grab rounded-full bg-slider-handle-bg shadow-md ring-2 ring-slider-handle-border ring-inset",
														!!isFocusVisible &&
															"outline-2 outline-focus-ring outline-offset-2",
														!!isDragging && "cursor-grabbing"
													)
												}
												index={index}
												key={index}
											>
												<AriaSliderOutput
													className={
														sortCx({
															output:
																"-translate-x-1/2 absolute top-2 left-1/2 translate-y-full whitespace-nowrap font-medium text-md text-primary",
														}).output
													}
												>
													{fractionLabel}
												</AriaSliderOutput>
											</AriaSliderThumb>
										);
									})}
								</>
							);
						}}
					</AriaSliderTrack>
				</AriaSlider>
			</div>
		</div>
	);
};
